import { LibrarySkeleton } from '@/components/Library/LibrarySkeleton';
import { VIDEO_TILE_WIDTH } from '@/components/VideoTile';
import { useBlockBrowserClose } from '@/hooks/useBlockBrowserClose';
import { useAvatarsFilters, useAvatarsSorting } from '@/pages/avatar';
import { useAllAvatars } from '@/pages/avatar/hooks/useAllAvatars';
import { useCreatePremiumPlusNotifications } from '@/pages/avatar/hooks/useCreatePremiumPlusNotifications';
import { AvatarStatus, PresenterUsecase } from '@d-id/types';
import styled from '@emotion/styled';
import { useEffect, useMemo } from 'react';
import { useAvatarListPageContext } from '../../contex';
import { DefaultLibrary } from './DefaultLibrary';
import { NotFoundBanner } from './NotFoundBanner';
import { UserLibrary } from './UserLibrary';

const LibraryContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

export const Library = () => {
    const { filter, search, setFiltersHidden } = useAvatarListPageContext();
    const { publicAvatars, userAvatars, isLoading, allAvatars } = useAllAvatars(PresenterUsecase.Video, 'all', true);
    useCreatePremiumPlusNotifications();

    const filteredAvatars = useAvatarsFilters(publicAvatars, filter, search);
    const filteredUserAvatars = useAvatarsFilters(userAvatars, filter, search);
    const { sortAvatars, sortAvatarsByDate } = useAvatarsSorting();

    const sortedAvatars = sortAvatars(filteredAvatars);
    const sortedUserAvatars = sortAvatarsByDate(filteredUserAvatars);

    const avatarsResultNotFound = !!search && !filteredAvatars.length;
    const userAvatarsResultNotFound = !!search && !filteredUserAvatars.length;
    const resultNotFound = avatarsResultNotFound && userAvatarsResultNotFound;
    const isUploadingAvatar = useMemo(() => allAvatars.some(a => a.status === AvatarStatus.Uploading), [allAvatars]);

    useBlockBrowserClose(() => isUploadingAvatar);

    useEffect(() => {
        setFiltersHidden(resultNotFound);
    }, [resultNotFound]);

    if (isLoading) {
        return (
            <LibraryContainer>
                <LibrarySkeleton amount={4} tileHeight="260px" tileWidth={`${VIDEO_TILE_WIDTH}px`} />
                <LibrarySkeleton amount={8} tileHeight="260px" tileWidth={`${VIDEO_TILE_WIDTH}px`} />
            </LibraryContainer>
        );
    }

    if (resultNotFound) {
        return <NotFoundBanner />;
    }

    return (
        <LibraryContainer>
            {!userAvatarsResultNotFound && <UserLibrary avatars={sortedUserAvatars} />}
            {!avatarsResultNotFound && <DefaultLibrary avatars={sortedAvatars} />}
        </LibraryContainer>
    );
};
